import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { setTitle, getAll } from '../../actions'
import styled from 'styled-components'
import Loading from '../../components/Loading'
import Card from '../../components/Card'
import formatNumbers from '../../utils/formatNumbers'
import moment from 'moment'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const DATE_FORMAT = 'YYYY-MM-DD'

const STATUS_LABELS = {
  created: 'Creado',
  Pendiente: 'Pendiente',
  PendienteReintento: 'Pendiente reintento',
  PendienteSurtido: 'Pendiente surtido',
  Enviado: 'Enviado',
  Facturado: 'Facturado',
  Entregado: 'Entregado',
  RequiereRevision: 'Requiere revisión',
  Cancelado: 'Cancelado',
}

const STATUS_COLORS = {
  created: '#718096',
  Pendiente: '#ed8936',
  PendienteReintento: '#dd6b20',
  PendienteSurtido: '#d69e2e',
  Enviado: '#3182ce',
  Facturado: '#38a169',
  Entregado: '#2f855a',
  RequiereRevision: '#c53030',
  Cancelado: '#9b2c2c',
}

const FALLBACK_COLOR = '#a0aec0'

const Dashboard = props => {
  const { setTitle, summary, timeseries } = props
  const [dateRange, setDateRange] = useState([
    moment().subtract(29, 'days').toDate(),
    moment().toDate(),
  ])
  const [rangeStart, rangeEnd] = dateRange

  useEffect(() => {
    setTitle({ title: 'Dashboard' })
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (!rangeStart || !rangeEnd) return

    const from = moment(rangeStart).format(DATE_FORMAT)
    const to = moment(rangeEnd).format(DATE_FORMAT)

    props.getAll(
      `dashboard/summary?from=${from}&to=${to}`,
      'GET_DASHBOARD_SUMMARY'
    )
    props.getAll(
      `dashboard/timeseries?from=${from}&to=${to}`,
      'GET_DASHBOARD_TIMESERIES'
    )
    // eslint-disable-next-line
  }, [rangeStart, rangeEnd])

  if (!summary) return <Loading />

  const { sales, ordersByStatus, users, topProducts, abandonedCarts } =
    summary

  const ordersByStatusData = {
    labels: ordersByStatus.map(
      item => STATUS_LABELS[item.status] || item.status
    ),
    datasets: [
      {
        label: 'Pedidos',
        data: ordersByStatus.map(item => item.count),
        backgroundColor: ordersByStatus.map(
          item => STATUS_COLORS[item.status] || FALLBACK_COLOR
        ),
      },
    ],
  }

  const topProductsData = {
    labels: topProducts.map(product => product.name),
    datasets: [
      {
        label: 'Unidades vendidas',
        data: topProducts.map(product => product.quantity),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
      },
    ],
  }

  const timeseriesData = timeseries && {
    labels: timeseries.series.map(item =>
      moment(item.date).format('DD/MM')
    ),
    datasets: [
      {
        label: 'Ventas ($)',
        data: timeseries.series.map(item => item.salesTotal),
        borderColor: 'rgba(72, 187, 120, 1)',
        backgroundColor: 'rgba(72, 187, 120, 0.2)',
        yAxisID: 'y',
        tension: 0.3,
      },
      {
        label: 'Pedidos',
        data: timeseries.series.map(item => item.ordersCount),
        borderColor: 'rgba(66, 153, 225, 1)',
        backgroundColor: 'rgba(66, 153, 225, 0.2)',
        yAxisID: 'y1',
        tension: 0.3,
      },
    ],
  }

  const timeseriesOptions = {
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        title: { display: true, text: 'Ventas ($)' },
      },
      y1: {
        type: 'linear',
        position: 'right',
        title: { display: true, text: 'Pedidos' },
        grid: { drawOnChartArea: false },
      },
    },
  }

  return (
    <DashboardStyled>
      <DateRangeBar>
        <DateRangeLabel>Periodo:</DateRangeLabel>
        <DatePicker
          selectsRange
          startDate={rangeStart}
          endDate={rangeEnd}
          onChange={update => setDateRange(update)}
          locale="es-MX"
          dateFormat="dd/MM/yyyy"
          maxDate={new Date()}
          isClearable={false}
        />
      </DateRangeBar>

      <Grid>
        <Item>
          <KpiValue>${formatNumbers(sales.total, 2)}</KpiValue>
          <Text>Ventas totales</Text>
        </Item>
        <Item color="primary">
          <KpiValue>{sales.count}</KpiValue>
          <Text>Cantidad de ventas</Text>
        </Item>
        <Item color="success">
          <KpiValue>{users.registered}</KpiValue>
          <Text>Usuarios registrados</Text>
        </Item>
        <Item color="warning">
          <KpiValue>{users.guest}</KpiValue>
          <Text>Usuarios invitado</Text>
        </Item>
        <LinkStyled to="/carritos-abandonados">
          <Item color="danger">
            <KpiValue>{abandonedCarts.count}</KpiValue>
            <Text>Carritos abandonados</Text>
          </Item>
        </LinkStyled>
      </Grid>

      <Card title="Ventas y pedidos por fecha">
        <ChartBox>
          {timeseriesData ? (
            <Line
              data={timeseriesData}
              options={{ ...timeseriesOptions, maintainAspectRatio: false }}
            />
          ) : (
            <Loading />
          )}
        </ChartBox>
      </Card>

      <ChartsGrid>
        <Card title="Pedidos por estatus">
          <ChartBox>
            {ordersByStatus.length ? (
              <Doughnut
                data={ordersByStatusData}
                options={{ maintainAspectRatio: false }}
              />
            ) : (
              <Empty>Sin pedidos todavía.</Empty>
            )}
          </ChartBox>
        </Card>
        <Card title="Top 10 productos más vendidos">
          <ChartBox>
            {topProducts.length ? (
              <Bar
                data={topProductsData}
                options={{
                  indexAxis: 'y',
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                }}
              />
            ) : (
              <Empty>Sin ventas registradas todavía.</Empty>
            )}
          </ChartBox>
        </Card>
      </ChartsGrid>

      <Card title="Top 10 productos más vendidos">
        <TableStyled>
          <thead>
            <tr>
              <Th>Modelo</Th>
              <Th>Producto</Th>
              <Th>Unidades vendidas</Th>
              <Th>Ingresos</Th>
            </tr>
          </thead>
          <tbody>
            {topProducts.map(product => (
              <tr key={product.model}>
                <Td>{product.model}</Td>
                <Td>{product.name}</Td>
                <Td>{product.quantity}</Td>
                <Td>${formatNumbers(product.revenue, 2)}</Td>
              </tr>
            ))}
            {!topProducts.length && (
              <tr>
                <Td colSpan={4}>Sin ventas registradas todavía.</Td>
              </tr>
            )}
          </tbody>
        </TableStyled>
      </Card>
    </DashboardStyled>
  )
}

const mapStateToProps = state => ({
  summary: state.dashboard.summary,
  timeseries: state.dashboard.timeseries,
})

const mapDispatchToProps = {
  setTitle,
  getAll,
}

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard)

const DashboardStyled = styled.div`
  display: flex;
  flex-direction: column;
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  padding-bottom: 1rem;
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
`

const Item = styled.div`
  position: relative;
  color: ${props => props.theme.text};
  background-color: ${props => props.theme.backgroundButton};
  border-bottom: 4px solid
    ${props => {
      switch (props.color) {
        case 'warning':
          return props.theme.warning
        case 'danger':
          return props.theme.danger
        case 'info':
          return props.theme.info
        case 'success':
          return props.theme.success
        case 'secondary':
          return props.theme.secondary
        case 'purple':
          return props.theme.purple
        default:
          return props.theme.primary
      }
    }};
  display: flex;
  flex-direction: column;
  padding: 1.5rem;
  border-radius: 5px;
`

const KpiValue = styled.span`
  line-height: 1.1;
  font-size: 1.8rem;
  font-weight: 500;
`

const Text = styled.span`
  font-size: 0.85rem;
  font-weight: 300;
  margin-top: 0.5rem;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`

const Empty = styled.p`
  opacity: 0.7;
`

const DateRangeBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`

const DateRangeLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 500;
`

const ChartBox = styled.div`
  height: 25vh;
  position: relative;
`

const TableStyled = styled.table`
  border-collapse: collapse;
  width: 100%;
`

const Th = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-size: 0.8rem;
  border-bottom: ${({ theme }) => theme.border};
`

const Td = styled.td`
  padding: 0.75rem;
  font-size: 0.8rem;
  border-bottom: ${({ theme }) => theme.border};
`

const LinkStyled = styled(Link)`
  text-decoration: none;
`
